import type { Spx } from '@wix/thunderbolt-becky-types'
import type { StylePropItem } from '@wix/thunderbolt-becky-root'

// Registry imports
import { initializeConverters, isConvertersRegistryInitialized } from './converters-init'
import { convertersRegistry, getConverter, hasConverter, getRegisteredConverterKeys } from './convertersRegistry'
import { NATIVE_CSS_PROPS } from './consts'

// Initialize converters registry on module load
initializeConverters()

/**
 * Converts a style property to CSS variables using the registry-based system.
 */
export const convertStyleProp = (styleProp: StylePropItem, propName: string, spx?: Spx): Record<string, string> => {
	const converter = getConverter(styleProp.type)

	if (!converter) {
		console.warn(
			`No converter found in registry for style property type: ${
				styleProp.type
			}. Available converters: ${getRegisteredConverterKeys().join(', ')}`
		)
		return {}
	}

	const cssProps = converter(styleProp, spx)

	if (typeof cssProps === 'string') {
		const propsKey =
			NATIVE_CSS_PROPS.has(propName) || styleProp.type === 'VariableReference' ? `${propName}` : `--${propName}`
		return { [propsKey]: cssProps }
	}

	const cssVars: Record<string, string> = {}
	Object.entries(cssProps).forEach(([key, subpropValue]) => {
		if (subpropValue) {
			if (key === styleProp.type) {
				// @ts-ignore
				cssVars[`--${propName}`] = subpropValue.replaceAll('{propName}', propName)
			} else {
				cssVars[`--${propName}-${key}`] = subpropValue
			}
		}
	})

	return cssVars
}

// Registry-based API exports
export {
	// Registry access
	convertersRegistry,
	getConverter,
	hasConverter,
	getRegisteredConverterKeys,
	// Initialization functions
	initializeConverters,
	isConvertersRegistryInitialized,
}

/**
 * Utility function to validate registry completeness during development
 */
export const validateRegistryCompleteness = (): {
	isComplete: boolean
	registeredCount: number
	registeredConverters: Array<string>
} => {
	const registeredConverters = getRegisteredConverterKeys()
	const registeredCount = registeredConverters.length

	return {
		isComplete: registeredCount > 0,
		registeredCount,
		registeredConverters: registeredConverters.sort(),
	}
}
